import * as React from "react"
import { Control } from "rete"
import { BooleanTileGrid, NumericTileGrid } from "../tile_grid"

/**
 * Display size of the preview well. Keep the backing canvas at this
 * resolution — never 1:1 with the tile grid.
 *
 * Large projects allocate rasters up to MAX_TILES (10M) cells. The old
 * preview set canvas.width/height to the full grid and issued a fillRect
 * per cell. Chrome's GPU-backed 2D canvas has a much tighter size/memory
 * cap than Firefox, so running those projects crashed Chrome while
 * Firefox survived.
 */
const PREVIEW_MAX_DIM = 200

interface PreviewProps {
  getTileGrid: () => BooleanTileGrid | NumericTileGrid
}

function previewCanvasSize(width: number, height: number): { w: number; h: number } {
  const w = Math.max(1, width || 1)
  const h = Math.max(1, height || 1)
  const scale = Math.min(PREVIEW_MAX_DIM / w, PREVIEW_MAX_DIM / h, 1)
  return {
    w: Math.max(1, Math.round(w * scale)),
    h: Math.max(1, Math.round(h * scale)),
  }
}

function paintPreview(
  ctx: CanvasRenderingContext2D,
  tileGrid: BooleanTileGrid | NumericTileGrid,
  outW: number,
  outH: number
) {
  const imageData = ctx.createImageData(outW, outH)
  const pixels = imageData.data
  const scaleX = tileGrid.width / outW
  const scaleY = tileGrid.height / outH
  const maxX = Math.max(0, tileGrid.width - 1)
  const maxY = Math.max(0, tileGrid.height - 1)

  if (tileGrid instanceof BooleanTileGrid) {
    for (let py = 0; py < outH; py++) {
      const gy = tileGrid.y + Math.min(maxY, Math.floor(py * scaleY))
      for (let px = 0; px < outW; px++) {
        const gx = tileGrid.x + Math.min(maxX, Math.floor(px * scaleX))
        const i = (py * outW + px) * 4
        const v = tileGrid.get(gx, gy) ? 255 : 0
        pixels[i] = v
        pixels[i + 1] = v
        pixels[i + 2] = v
        pixels[i + 3] = 255
      }
    }
  } else if (tileGrid instanceof NumericTileGrid) {
    let [min, max] = tileGrid.getMinMax()
    // if min === max, this returns a blank canvas. Fix by setting min to max - 1
    if (min === max) min -= 1
    const range = max - min
    for (let py = 0; py < outH; py++) {
      const gy = tileGrid.y + Math.min(maxY, Math.floor(py * scaleY))
      for (let px = 0; px < outW; px++) {
        const gx = tileGrid.x + Math.min(maxX, Math.floor(px * scaleX))
        const i = (py * outW + px) * 4
        const val = tileGrid.get(gx, gy)
        if (!isFinite(val)) {
          pixels[i] = 255
          pixels[i + 1] = 0
          pixels[i + 2] = 0
          pixels[i + 3] = 25
        } else {
          const a = (val - min) / range
          pixels[i] = 255
          pixels[i + 1] = 255
          pixels[i + 2] = 255
          pixels[i + 3] = Math.round(a * 255)
        }
      }
    }
  }

  ctx.putImageData(imageData, 0, 0)
}

const Preview = ({ getTileGrid }: PreviewProps) => {
  const tileGrid = getTileGrid()
  const canvasRef = React.useRef<HTMLCanvasElement>()
  const { w: outW, h: outH } = previewCanvasSize(tileGrid.width, tileGrid.height)

  React.useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.width = outW
    canvas.height = outH
    const ctx = canvas.getContext("2d")
    if (ctx === null || ctx === undefined) return
    try {
      paintPreview(ctx, tileGrid, outW, outH)
    } catch {
      // Chrome throws if the (now small) context is lost; skip the thumb.
    }
  })

  const landscape = tileGrid.width >= tileGrid.height
  return <div style={{width: "200px", height: "200px", margin: "0px auto", backgroundColor: "rgba(0, 0, 0, 0.2)", textAlign: "center", lineHeight: "200px"}}>
    <canvas
      style={landscape ?
      {
        verticalAlign: 'middle',
        width: '200px',
        height: `${200 * tileGrid.height / Math.max(tileGrid.width, 1)}px`,
        imageRendering: 'pixelated',
      } : {
        verticalAlign: 'middle',
        width: `${200 * tileGrid.width / Math.max(tileGrid.height, 1)}px`,
        height: '200px',
        imageRendering: 'pixelated',
      }}
      width={outW}
      height={outH}
      ref={canvasRef as any}
    ></canvas>
  </div>
}

export class PreviewControl extends Control {
  props: PreviewProps
  component: (props: PreviewProps) => JSX.Element
  
  constructor(getTileGrid: () => BooleanTileGrid | NumericTileGrid) {
    super("Preview")
    this.props = { getTileGrid }
    this.component = Preview
  }
}
